import type { JSX } from 'react';

import clsx from 'clsx';
import NextLink from 'next/link';

import { horizontalPaddingClasses } from '@/components/classes/horizontalPaddingClasses';
import { hoverColorClasses } from '@/components/classes/hoverColorClasses';
import { variantClasses } from '@/components/classes/variantClasses';
import { verticalPaddingClasses } from '@/components/classes/verticalPaddingClasses';
import { testIds } from '@/components/constants/testIds';
import { composeJustifyClasses } from '@/components/functions/composeJustifyClasses';
import { composeRadiusClasses } from '@/components/functions/composeRadiusClasses';
import { composeWidthClasses } from '@/components/functions/composeWidthClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { Color } from '@/components/types/Color';
import type { FontWeight } from '@/components/types/FontWeight';
import type { IconType } from '@/components/types/IconType';
import type { Justify } from '@/components/types/Justify';
import type { PaddingSize } from '@/components/types/PaddingSize';
import type { Radius } from '@/components/types/Radius';
import type { Target } from '@/components/types/Target';
import type { TextSize } from '@/components/types/TextSize';
import type { Variant } from '@/components/types/Variant';
import type { Width } from '@/components/types/Width';
import { componentsConfig } from '@/config/componentsConfig';

import HStack from '../HStack';
import Icon from '../Icon';
import Spinner from '../Spinner';
import Text from '../Text';

type LinkProps = {
    children: React.ReactNode | string;
    href: string;
    variant?: Variant;
    color?: Color;
    hoverColor?: Color;
    size?: Adaptive<TextSize>;
    weight?: Adaptive<FontWeight>;
    horizontalPadding?: PaddingSize;
    verticalPadding?: PaddingSize;
    justify?: Adaptive<Justify>;
    radius?: Adaptive<Radius>;
    width?: Adaptive<Width>;
    prefixIcon?: IconType;
    suffixIcon?: IconType;
    isLoading?: boolean;
    loadingText?: string;
    isDisabled?: boolean;
    isDimmed?: boolean;
    target?: Target;
    className?: string;
    iconClassName?: string;
    testId?: string;
};

export default function Link({
    children,
    href,
    variant = componentsConfig.link.variant,
    color = componentsConfig.link.color,
    hoverColor = componentsConfig.link.hoverColor,
    size = componentsConfig.link.size,
    weight = componentsConfig.link.weight,
    horizontalPadding = componentsConfig.link.horizontalPadding,
    verticalPadding = componentsConfig.link.verticalPadding,
    justify = componentsConfig.link.justify,
    radius = componentsConfig.link.radius,
    width = componentsConfig.link.width,
    target = componentsConfig.link.target,
    prefixIcon,
    suffixIcon,
    isLoading,
    loadingText,
    isDisabled,
    isDimmed = componentsConfig.link.isDimmed,
    className,
    iconClassName,
    testId = testIds.link.base,
}: LinkProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const commonClasses = 'inline-flex align-middle items-center gap-2 focus:outline-none focus:ring-0 focus:ring-offset-0';

    const spinnerColor: Record<string, 'accent' | 'white'> = {
        solid: 'white',
        soft: 'accent',
        outline: 'accent',
        ghost: 'accent',
        link: 'accent',
    };

    // ----------------------------------------------------------------------------------------------------
    // MARK: Subcomponents
    // ----------------------------------------------------------------------------------------------------
    function renderChildren(): React.ReactNode | string {
        if (isLoading) {
            return (
                <HStack gap={2} width="auto">
                    <Spinner testId={testIds.link.spinner} color={spinnerColor[variant]} size={4} />
                    <Text testId={testIds.link.loadingText} size={size} weight={weight} color="none">
                        {loadingText ?? children}
                    </Text>
                </HStack>
            );
        }

        if (typeof children === 'string') {
            return (
                <Text testId={testIds.link.text} size={size} weight={weight} color="none">
                    {children}
                </Text>
            );
        }

        return children;
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <NextLink
            data-testid={testId}
            href={href}
            target={target}
            className={clsx(
                commonClasses,
                variantClasses[variant][color],
                !isDisabled && hoverColorClasses[variant][hoverColor],
                variant !== 'link' && horizontalPaddingClasses[horizontalPadding],
                variant !== 'link' && verticalPaddingClasses[verticalPadding],
                composeJustifyClasses(justify),
                composeRadiusClasses(radius),
                composeWidthClasses(width),
                (isLoading || isDisabled) && 'pointer-events-none',
                (isLoading || isDisabled) && isDimmed && 'opacity-50',
                className
            )}
        >
            {prefixIcon && <Icon testId={testIds.link.prefixIcon} icon={prefixIcon} className={iconClassName} />}

            {renderChildren()}

            {suffixIcon && <Icon testId={testIds.link.suffixIcon} icon={suffixIcon} className={iconClassName} />}
        </NextLink>
    );
}
